import React, { createContext, useContext, useState, useEffect, useRef, useMemo, useCallback } from 'react';
import casinoWS from '../services/websocket';

const PlayerIdentityContext = createContext(null);
const PlayerProgressContext = createContext(null);

export const PlayerProvider = ({ children }) => {
  // Identity state — changes only on init/reconnect
  const [steamId, setSteamId] = useState(null);
  const steamIdRef = useRef(null);
  const [playerName, setPlayerName] = useState('Player');
  const [avatarUrl, setAvatarUrl] = useState(null);

  // Progress state — changes on every bet/level event
  const [progress, setProgress] = useState({
    level: 1,
    points: 0,
    currentXp: 0,
    totalXp: 0,
    xpForNextLevel: 100,
    prestigeCount: 0,
    xpMultiplier: 1,
    unlockedItems: ['game_blackjack'],
    xpStats: {},
    lastXpGain: null,
    pendingLevelUp: null,
    ownedEmotes: [],
  });

  const setPoints = useCallback((val) => {
    setProgress(prev => {
      const newPoints = typeof val === 'function' ? val(prev.points) : val;
      return newPoints === prev.points ? prev : { ...prev, points: newPoints };
    });
  }, []);

  const setOwnedEmotes = useCallback((val) => {
    setProgress(prev => {
      const newEmotes = typeof val === 'function' ? val(prev.ownedEmotes) : val;
      return newEmotes === prev.ownedEmotes ? prev : { ...prev, ownedEmotes: newEmotes };
    });
  }, []);

  const clearLevelUp = useCallback(() => {
    setProgress(prev => prev.pendingLevelUp ? { ...prev, pendingLevelUp: null } : prev);
  }, []);

  useEffect(() => {
    const unsubscribers = [];

    unsubscribers.push(casinoWS.on('init', (data) => {
      setSteamId(data.steamId);
      steamIdRef.current = data.steamId;
      setPlayerName(data.name);
      if (data.avatarUrl) setAvatarUrl(data.avatarUrl);
      setProgress(prev => ({
        ...prev,
        ...(data.level !== undefined && { level: data.level }),
        ...(data.points !== undefined && { points: data.points }),
        ...(data.currentXp !== undefined && { currentXp: data.currentXp }),
        ...(data.totalXp !== undefined && { totalXp: data.totalXp }),
        ...(data.xpForNextLevel !== undefined && { xpForNextLevel: data.xpForNextLevel }),
        ...(data.prestigeCount !== undefined && { prestigeCount: data.prestigeCount }),
        ...(data.xpMultiplier !== undefined && { xpMultiplier: data.xpMultiplier }),
        ...(data.unlockedItems && { unlockedItems: data.unlockedItems }),
        ...(data.xpStats && { xpStats: data.xpStats }),
        ...(data.ownedEmotes && { ownedEmotes: data.ownedEmotes }),
      }));
    }));

    unsubscribers.push(casinoWS.on('levelUpdate', (data) => {
      setProgress(prev => ({
        ...prev,
        ...(data.level !== undefined && { level: data.level }),
        ...(data.currentXp !== undefined && { currentXp: data.currentXp }),
        ...(data.totalXp !== undefined && { totalXp: data.totalXp }),
        ...(data.xpForNextLevel !== undefined && { xpForNextLevel: data.xpForNextLevel }),
        ...(data.xpAwarded && {
          lastXpGain: { amount: data.xpAwarded, source: data.xpSource || 'game', timestamp: Date.now() },
        }),
        ...(data.levelUp && {
          pendingLevelUp: data.levelUp,
          // Update unlocked items if new unlocks came in
          ...(data.levelUp.newUnlocks?.length > 0 && {
            unlockedItems: [...new Set([...prev.unlockedItems, ...data.levelUp.newUnlocks])],
          }),
        }),
      }));
    }));

    unsubscribers.push(casinoWS.on('pointsUpdate', (data) => {
      if (data.points !== undefined) {
        setProgress(prev => ({ ...prev, points: data.points }));
      }
    }));

    unsubscribers.push(casinoWS.on('shopPurchased', (data) => {
      setProgress(prev => {
        const updates = {};
        if (data.newPoints !== undefined) updates.points = data.newPoints;
        if (data.itemId && data.itemId.startsWith('emote_')) {
          updates.ownedEmotes = prev.ownedEmotes.includes(data.itemId)
            ? prev.ownedEmotes
            : [...prev.ownedEmotes, data.itemId];
        }
        return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev;
      });
    }));

    return () => unsubscribers.forEach(unsub => unsub());
  }, []);

  const identityValue = useMemo(() => ({
    steamId,
    steamIdRef,
    playerName,
    avatarUrl,
  }), [steamId, playerName, avatarUrl]);

  const progressValue = useMemo(() => ({
    level: progress.level,
    points: progress.points,
    setPoints,
    currentXp: progress.currentXp,
    totalXp: progress.totalXp,
    xpForNextLevel: progress.xpForNextLevel,
    prestigeCount: progress.prestigeCount,
    xpMultiplier: progress.xpMultiplier,
    unlockedItems: progress.unlockedItems,
    xpStats: progress.xpStats,
    lastXpGain: progress.lastXpGain,
    pendingLevelUp: progress.pendingLevelUp,
    clearLevelUp,
    ownedEmotes: progress.ownedEmotes,
    setOwnedEmotes,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [progress.level, progress.points, progress.currentXp, progress.totalXp,
      progress.xpForNextLevel, progress.prestigeCount, progress.xpMultiplier,
      progress.unlockedItems, progress.xpStats,
      progress.lastXpGain, progress.pendingLevelUp,
      progress.ownedEmotes,
      setPoints, setOwnedEmotes, clearLevelUp]);

  return (
    <PlayerIdentityContext.Provider value={identityValue}>
      <PlayerProgressContext.Provider value={progressValue}>
        {children}
      </PlayerProgressContext.Provider>
    </PlayerIdentityContext.Provider>
  );
};

export const usePlayerIdentity = () => {
  const context = useContext(PlayerIdentityContext);
  if (!context) {
    throw new Error('usePlayerIdentity must be used within a PlayerProvider');
  }
  return context;
};

export const usePlayerProgress = () => {
  const context = useContext(PlayerProgressContext);
  if (!context) {
    throw new Error('usePlayerProgress must be used within a PlayerProvider');
  }
  return context;
};

// Convenience hook — returns both merged (backwards compat)
export const usePlayer = () => {
  const identity = useContext(PlayerIdentityContext);
  const progress = useContext(PlayerProgressContext);
  if (!identity || !progress) {
    throw new Error('usePlayer must be used within a PlayerProvider');
  }
  return { ...identity, ...progress };
};

export default PlayerIdentityContext;
